/**
 * useDispatchAlerts Hook
 *
 * React Query hook for fetching and managing dispatcher alerts.
 * Used by AlertsBar component.
 *
 * Features:
 * - 2-minute fallback polling (socket events provide real-time updates)
 * - Pauses polling when tab is hidden, refetches on focus
 * - Dismiss alert mutation
 * - Complete patrol mutation
 * - Transform backend alerts to frontend format
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import {
  fetchAlerts,
  dismissAlert as dismissAlertApi,
  completePatrol as completePatrolApi,
  startPatrol as startPatrolApi,
  AlertsResponse,
  AlertResponse,
  AlertsParams,
} from '../../../../../api/dispatch';
import {
  DispatchAlert,
  AlertType,
  AlertSeverity,
} from '../../components/guardLocation/AlertsBar';
import { DISPATCH_STATS_QUERY_KEY } from './useDispatchStats';
import { ACTIVITY_FEED_QUERY_KEY } from './useActivityFeed';

/** Query key for alerts */
export const DISPATCH_ALERTS_QUERY_KEY = ['dispatch', 'alerts'];

/** Polling interval (2 minutes — socket events handle real-time, polling is fallback) */
const REFETCH_INTERVAL = 2 * 60 * 1000;

/** Stale time (60 seconds) */
const STALE_TIME = 60 * 1000;

export interface UseDispatchAlertsOptions {
  /** Filter by alert types (comma-separated) */
  types?: string;
  /** Include dismissed alerts */
  includeDismissed?: boolean;
  /** Maximum alerts to fetch */
  limit?: number;
  /** Override polling interval (ms) */
  refetchInterval?: number;
  /** Disable auto-polling */
  enablePolling?: boolean;
}

export interface UseDispatchAlertsResult {
  /** Alerts transformed for frontend */
  alerts: DispatchAlert[];
  /** Raw API response */
  rawData: AlertsResponse | null;
  /** Total alert count */
  totalCount: number;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether any fetch is in progress */
  isFetching: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if present */
  error: Error | null;
  /** Last successful update timestamp */
  dataUpdatedAt: number | undefined;
  /** Manually trigger a refresh */
  refetch: () => void;
  /** Dismiss an alert */
  dismissAlert: (alertId: number) => Promise<void>;
  /** Whether dismiss mutation is pending */
  isDismissing: boolean;
  /** Complete a patrol by setting its end_date */
  completePatrol: (commentListId: number) => Promise<void>;
  /** Whether complete mutation is pending */
  isCompleting: boolean;
}

/**
 * Transform backend alert to frontend DispatchAlert format
 */
function transformAlert(alert: AlertResponse): DispatchAlert {
  // Map backend types to frontend types
  const typeMap: Record<string, AlertType> = {
    no_activity: 'no_activity',
    low_battery: 'low_battery',
    incomplete_patrol: 'incomplete_patrol',
    stale_patrol: 'stale_patrol',
    left_site: 'left_site',
    emergency_sos: 'emergency_sos',
    vms_incident: 'vms_incident',
  };

  return {
    alertId: String(alert.alertId),
    type: typeMap[alert.type] || 'no_activity',
    severity: alert.severity as AlertSeverity,
    guardId: alert.guardId,
    guardName: alert.guardName,
    message: alert.message,
    details: {
      minutesSinceActivity: alert.details.minutesSinceActivity,
      batteryPercent: alert.details.batteryPercent,
      siteName: alert.details.siteName,
      patrolId: alert.details.patrolId,
      startedAt: alert.details.patrolStartedAt,
      // VMS incident fields
      incidentId: alert.details.incidentId,
      cameraId: alert.details.cameraId,
      cameraName: alert.details.cameraName,
      incidentType: alert.details.incidentType,
      thumbnailUrl: alert.details.thumbnailUrl,
      siteId: alert.details.siteId,
    },
    createdAt: alert.createdAt,
    dismissedAt: alert.dismissedAt,
  };
}

/**
 * Hook to fetch and manage dispatcher alerts
 *
 * @param options - Configuration options
 * @returns Alerts with loading/error states and mutation functions
 */
export function useDispatchAlerts(
  options: UseDispatchAlertsOptions = {}
): UseDispatchAlertsResult {
  const {
    types,
    includeDismissed = false,
    limit = 100,
    refetchInterval = REFETCH_INTERVAL,
    enablePolling = true,
  } = options;

  const queryClient = useQueryClient();

  const params: AlertsParams = {
    types,
    dismissed: includeDismissed,
    limit,
  };

  // Fetch alerts query
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: [...DISPATCH_ALERTS_QUERY_KEY, params],
    queryFn: () => fetchAlerts(params),
    refetchInterval: enablePolling ? refetchInterval : false,
    staleTime: STALE_TIME,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  // Dismiss alert mutation
  const dismissMutation = useMutation({
    mutationFn: (alertId: number) => dismissAlertApi(alertId),
    onSuccess: () => {
      // Invalidate alerts query to refetch
      queryClient.invalidateQueries({ queryKey: DISPATCH_ALERTS_QUERY_KEY });
      // Also invalidate stats as alert count may have changed
      queryClient.invalidateQueries({ queryKey: DISPATCH_STATS_QUERY_KEY });
    },
  });

  // Complete patrol mutation
  const completeMutation = useMutation({
    mutationFn: ({ commentListId }: { commentListId: number }) =>
      completePatrolApi(commentListId),
    onSuccess: () => {
      // Invalidate multiple queries after completing patrol
      queryClient.invalidateQueries({ queryKey: DISPATCH_ALERTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: DISPATCH_STATS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ACTIVITY_FEED_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['guardLocations'] });
    },
  });

  // Start patrol mutation (Dispatcher Start Patrol)
  const startMutation = useMutation({
    mutationFn: ({ patrolId, note }: { patrolId: number; note?: string }) =>
      startPatrolApi(patrolId, { note }),
    onSuccess: () => {
      // Invalidate multiple queries after starting patrol
      queryClient.invalidateQueries({ queryKey: DISPATCH_ALERTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: DISPATCH_STATS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ACTIVITY_FEED_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['guardLocations'] });
      queryClient.invalidateQueries({ queryKey: ['guardShift'] });
    },
  });

  // Transform alerts to frontend format
  const alerts = useMemo(() => {
    if (!data?.alerts) return [];
    return data.alerts.map(transformAlert);
  }, [data?.alerts]);

  // Wrapper for dismiss mutation
  const dismissAlert = useCallback(
    async (alertId: number) => {
      await dismissMutation.mutateAsync(alertId);
    },
    [dismissMutation]
  );

  // Wrapper for complete mutation
  const completePatrol = useCallback(
    async (commentListId: number) => {
      await completeMutation.mutateAsync({ commentListId });
    },
    [completeMutation]
  );

  // Wrapper for start mutation
  const startPatrol = useCallback(
    async (patrolId: number, note?: string) => {
      await startMutation.mutateAsync({ patrolId, note });
    },
    [startMutation]
  );

  return {
    alerts,
    rawData: data ?? null,
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
    isError,
    error: error instanceof Error ? error : null,
    dataUpdatedAt,
    refetch,
    dismissAlert,
    isDismissing: dismissMutation.isPending,
    completePatrol,
    isCompleting: completeMutation.isPending,
    startPatrol,
    isStarting: startMutation.isPending,
  };
}

export default useDispatchAlerts;
